import { useEffect, useMemo, useRef, useState } from 'react';
import type { SurveyConfig } from './types';
import { findSurveyMatches, type ReplacementResult, type SearchMatch, type SearchOptions, type SearchScope } from './find-replace';
import '../../styles/find-replace.css';

type Props = {
  open: boolean;
  mode: 'find' | 'replace';
  requestId: number;
  config: SurveyConfig;
  disabled: boolean;
  canUndo: boolean;
  onModeChange: (mode: 'find' | 'replace') => void;
  onClose: () => void;
  onReplace: (matches: SearchMatch[], replacement: string) => ReplacementResult;
  onUndo: () => void;
};

export default function FindReplacePanel({ open, mode, requestId, config, disabled, canUndo, onModeChange, onClose, onReplace, onUndo }: Props) {
  const [query, setQuery] = useState('');
  const [replacement, setReplacement] = useState('');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeField, setWholeField] = useState(false);
  const [scope, setScope] = useState<SearchScope>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [feedback, setFeedback] = useState<{ text: string; error?: boolean } | null>(null);
  const [locateRequest, setLocateRequest] = useState(0);
  const queryRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const options: SearchOptions = useMemo(() => ({ query, caseSensitive, wholeField, scope }), [query, caseSensitive, wholeField, scope]);
  const matches = useMemo(() => open ? findSurveyMatches(config, options) : [], [open, config, options]);
  const selectedIndex = Math.max(0, matches.findIndex(match => match.key === selectedKey));
  const active = matches[selectedIndex];
  const fieldCount = new Set(matches.map(match => match.field.key)).size;

  useEffect(() => {
    if (!open) return;
    queryRef.current?.focus({ preventScroll: true });
    queryRef.current?.select();
  }, [open, requestId]);

  useEffect(() => { setSelectedKey(null); setFeedback(null); }, [options]);

  useEffect(() => {
    if (!open || !active) return;
    const field = active.field;
    const container = field.questionId
      ? Array.from(document.querySelectorAll<HTMLElement>('[data-question-id]')).find(element => element.dataset.questionId === field.questionId)
      : document.querySelector<HTMLElement>('.editor-head');
    const target = container && (Array.from(container.querySelectorAll<HTMLElement>('[data-find-field]')).find(element => element.dataset.findField === field.domKey) ?? container);
    if (!target) return;
    target.classList.add('find-replace-target');
    const topbar = document.querySelector('.editor-layout > .topbar');
    const top = window.scrollY + target.getBoundingClientRect().top - (topbar?.getBoundingClientRect().height ?? 56) - 24;
    // Keep the field above the bottom sheet on compact screens. Do not steal the search input's focus.
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
    return () => target.classList.remove('find-replace-target');
  }, [open, active?.key, active?.field.text, locateRequest]);

  useEffect(() => {
    if (!showResults) return;
    const list = resultsRef.current;
    const row = list?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (list && row) {
      const top = row.offsetTop - list.offsetTop;
      if (top < list.scrollTop || top + row.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = Math.max(0, top - 12);
    }
  }, [showResults, active?.key]);

  const move = (direction: number) => {
    if (!matches.length) return;
    setSelectedKey(matches[(selectedIndex + direction + matches.length) % matches.length].key);
    setLocateRequest(value => value + 1);
    setFeedback(null);
  };

  const replace = (all: boolean) => {
    if (!active || disabled) return;
    try {
      const result = onReplace(all ? matches : [active], replacement);
      setFeedback({ text: result.count ? `已替换 ${result.count} 处。点击顶部“保存”后生效。` : '替换内容与原文相同，未作修改。' });
      if (!all && result.count) {
        const updated = findSurveyMatches(result.config, options);
        const fields = Array.from(new Set(matches.map(match => match.field.key)));
        const fieldIndex = fields.indexOf(active.field.key);
        const next = updated.find(match => (match.field.key === active.field.key && match.start >= active.start + replacement.length) || fields.indexOf(match.field.key) > fieldIndex);
        setSelectedKey((next ?? updated[0])?.key ?? null);
      }
      setLocateRequest(value => value + 1);
    } catch (error) {
      setFeedback({ text: error instanceof Error ? error.message : '替换失败，请重试。', error: true });
    }
  };

  if (!open) return null;
  return (
    <section
      id="editor-find-replace"
      className="find-replace-panel"
      role="dialog"
      aria-labelledby="find-replace-title"
      onKeyDown={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onClose(); }
      }}
    >
      <div className="find-replace-heading">
        <h2 id="find-replace-title">查找替换</h2>
        <button type="button" className="find-replace-close" aria-label="关闭查找替换" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
        </button>
      </div>
      <div className="find-replace-tabs" role="tablist" aria-label="查找替换模式">
        {(['find', 'replace'] as const).map(value => (
          <button key={value} type="button" role="tab" id={`find-replace-tab-${value}`} aria-selected={mode === value} aria-controls="find-replace-fields" tabIndex={mode === value ? 0 : -1}
            onClick={() => onModeChange(value)}
            onKeyDown={event => {
              if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                const next = event.key === 'Home' ? 'find' : event.key === 'End' ? 'replace' : mode === 'find' ? 'replace' : 'find';
                onModeChange(next);
                document.getElementById(`find-replace-tab-${next}`)?.focus();
              }
            }}>{value === 'find' ? '查找' : '替换'}</button>
        ))}
      </div>
      <div id="find-replace-fields" role="tabpanel" aria-labelledby={`find-replace-tab-${mode}`} className="find-replace-fields">
        <label htmlFor="find-replace-query">查找内容</label>
        <input ref={queryRef} id="find-replace-query" className="input" value={query} placeholder="输入要查找的文字" autoComplete="off"
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.nativeEvent.isComposing && event.keyCode !== 229) { event.preventDefault(); move(event.shiftKey ? -1 : 1); }
          }} />
        {mode === 'replace' && <>
          <label htmlFor="find-replace-value">替换为</label>
          <input id="find-replace-value" className="input" value={replacement} placeholder="留空则删除匹配文字" autoComplete="off" onChange={event => { setReplacement(event.target.value); setFeedback(null); }} />
        </>}
        <label htmlFor="find-replace-scope">查找范围</label>
        <select id="find-replace-scope" className="input" value={scope} onChange={event => setScope(event.target.value as SearchScope)}>
          <option value="all">全部题目文字（含问卷标题、描述）</option>
          <option value="titles">问卷标题 / 题干 / 标题说明</option>
          <option value="descriptions">问卷描述 / 题目描述</option>
          <option value="options">选项 / 矩阵行列文字</option>
          <option value="labels">刻度文字 / 单位 / 选择提示</option>
        </select>
        <div className="find-replace-options">
          <label><input type="checkbox" checked={caseSensitive} onChange={event => setCaseSensitive(event.target.checked)} />区分大小写</label>
          <label><input type="checkbox" checked={wholeField} onChange={event => setWholeField(event.target.checked)} />完整匹配</label>
        </div>
        <div className="find-replace-navigation">
          <button type="button" className="btn" disabled={!matches.length} onClick={() => move(-1)}>上一个</button>
          <button type="button" className="btn" disabled={!matches.length} onClick={() => move(1)}>下一个</button>
          <button type="button" className="btn" disabled={!query} aria-expanded={showResults} onClick={() => setShowResults(value => !value)}>{showResults ? '收起结果' : '查找全部'}</button>
        </div>
        {mode === 'replace' && <div className="find-replace-actions">
          <button type="button" className="btn" disabled={!active || disabled} onClick={() => replace(false)}>替换当前</button>
          <button type="button" className="btn btn-primary" disabled={!active || disabled} onClick={() => replace(true)}>全部替换</button>
          <button type="button" className="find-replace-undo" disabled={!canUndo || disabled} title="撤销最近一次替换；继续手动编辑后不可撤销" onClick={() => { onUndo(); setFeedback({ text: '已撤销上次替换。点击顶部“保存”可保存恢复后的内容。' }); }}>撤销替换</button>
        </div>}
      </div>
      <div className="find-replace-summary" role="status" aria-live="polite">
        {!query ? '输入文字开始查找' : matches.length ? `第 ${selectedIndex + 1} / ${matches.length} 处 · 共 ${fieldCount} 个文字项` : '未找到匹配内容'}
      </div>
      {feedback && <div className={`find-replace-feedback${feedback.error ? ' is-error' : ''}`} role={feedback.error ? 'alert' : 'status'}>{feedback.text}</div>}
      {active && !showResults && <button type="button" className="find-replace-result is-current" onClick={() => setLocateRequest(value => value + 1)} aria-label={`定位到${active.field.location} · ${active.field.label}`}>
        <span className="find-replace-location">{active.field.location} · {active.field.label}</span>
        <MatchExcerpt match={active} />
      </button>}
      {showResults && matches.length > 0 && <div ref={resultsRef} className="find-replace-results" role="group" aria-label="全部查找结果">
        {matches.map((match, index) => <button key={match.key} type="button" className="find-replace-result" aria-pressed={index === selectedIndex}
          onClick={() => { setSelectedKey(match.key); setLocateRequest(value => value + 1); setFeedback(null); }}>
          <span className="find-replace-location">{index + 1}. {match.field.location} · {match.field.label}</span>
          <MatchExcerpt match={match} />
        </button>)}
      </div>}
      <p className="find-replace-hint">Enter 下一处，Shift + Enter 上一处，Esc 关闭。按输入的文字查找，完整匹配指整个文字项一致。</p>
    </section>
  );
}

function MatchExcerpt({ match }: { match: SearchMatch }) {
  const { field, start, end } = match;
  const from = Math.max(0, start - 22);
  const to = Math.min(field.text.length, end + 38);
  return <span className="find-replace-excerpt">{from > 0 ? '…' : ''}{field.text.slice(from, start)}<mark>{field.text.slice(start, end)}</mark>{field.text.slice(end, to)}{to < field.text.length ? '…' : ''}</span>;
}
